<template>
  <section class="about-page" :dir="pageDirection">
    <h1>{{ tr("title") }}</h1>

    <div class="about-card">
      <div class="logos">
        <div class="logo-item ministry-logo">
          <img src="@/assets/yemen-logo.png" :alt="tr('logos.ministry')" />
        </div>

        <div class="logo-item">
          <img src="@/assets/unops-logo.png" :alt="tr('logos.unops')" />
        </div>

        <div class="logo-item">
          <img src="@/assets/afcar-logo.png" :alt="tr('logos.afcar')" />
        </div>
      </div>

      <h2>{{ tr("systemName") }}</h2>

      <p>{{ tr("description") }}</p>

      <div class="version">
        <span>{{ tr("version") }}</span>
        <span>{{ tr("assessmentDate") }}</span>
        <span>{{ tr("reportReference") }}</span>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

const tr = (key, params = {}) => t(`aboutPage.${key}`, params);

const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));
</script>

<style scoped>
.about-page {
  min-height: 70vh;
  padding: 34px 38px 60px;
  overflow-x: hidden;
  background: #f4f6f9;
  color: #061226;
  font-family: "Inter", "Cairo", sans-serif;
}

.about-page[dir="rtl"] {
  font-family: "Cairo", "Inter", sans-serif;
}

.about-page h1 {
  margin: 0 0 22px;
  color: #0f2340;
  font-size: 28px;
  font-weight: 900;
  line-height: 1.5;
}

.about-page[dir="rtl"] h1 {
  text-align: right;
}

.about-page[dir="ltr"] h1 {
  text-align: left;
}

.about-card {
  padding: 42px 32px;
  text-align: center;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(15, 35, 64, 0.05);
}

.logos {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 34px;
  margin-bottom: 28px;
}

.logo-item {
  display: flex;
  min-width: 150px;
  min-height: 96px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
}

.logo-item img {
  display: block;
  width: auto;
  max-width: 260px;
  max-height: 82px;
  object-fit: contain;
}

.ministry-logo {
  border-color: #d6e0ec;
  background: #f8fafc;
}

.ministry-logo img {
  max-width: 115px;
  max-height: 88px;
}

.about-card h2 {
  max-width: 900px;
  margin: 0 auto 18px;
  color: #0f2340;
  font-size: 22px;
  font-weight: 900;
  line-height: 1.7;
}

.about-card p {
  max-width: 830px;
  margin: 0 auto;
  color: #334155;
  font-size: 15px;
  line-height: 2;
}

.version {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 14px;
  margin-top: 24px;
  color: #64748b;
  font-size: 13px;
}

.version span {
  position: relative;
}

.version span:not(:last-child)::after {
  content: "|";
  margin-inline-start: 14px;
  color: #cbd5e1;
}

@media (max-width: 800px) {
  .about-page {
    min-height: 100vh;
    padding: 18px 10px 42px;
  }

  .about-page h1 {
    margin-bottom: 16px;
    font-size: 22px;
  }

  .about-card {
    padding: 20px 14px;
    border-radius: 15px;
  }

  .logos {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 20px;
  }

  .logo-item {
    width: 100%;
    min-width: 0;
    min-height: 82px;
    box-sizing: border-box;
    padding: 9px 12px;
  }

  .logo-item img {
    max-width: 210px;
    max-height: 60px;
  }

  .ministry-logo {
    order: -1;
    min-height: 100px;
  }

  .ministry-logo img {
    max-width: 100px;
    max-height: 82px;
  }

  .about-card h2 {
    font-size: 18px;
    line-height: 1.7;
  }

  .about-card p {
    font-size: 13px;
    line-height: 1.9;
    text-align: start;
  }

  .version {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    margin-top: 20px;
    padding: 12px;
    text-align: start;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
  }

  .version span:not(:last-child)::after {
    display: none;
  }
}

@media (max-width: 380px) {
  .about-page {
    padding-inline: 8px;
  }

  .about-card {
    padding-inline: 12px;
  }

  .about-card h2 {
    font-size: 17px;
  }
}
</style>
